import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { Button } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import PropTypes from 'prop-types';

import TeoAcenando from '~/assets/images/teo_acenando.png';
import TeoApontando from '~/assets/images/teo_apontando.png';
import { t } from '~/helpers';

import * as S from './styles';

const START_DELAY = 500;
const LAYOUT_TRANSITION_TIME = 350;

export default function TeoOnboarding({
  targetRef,
  layoutKey,
  onComplete,
  onStepChange,
}) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  const updateTargetRect = useCallback(() => {
    if (!targetRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, [targetRef]);

  useEffect(() => {
    const timer = window.setTimeout(() => setStep(1), START_DELAY);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    onStepChange(step);
  }, [onStepChange, step]);

  useEffect(
    () => () => {
      onStepChange(0);
    },
    [onStepChange]
  );

  useLayoutEffect(() => {
    updateTargetRect();

    const transitionTimer = window.setTimeout(
      updateTargetRect,
      LAYOUT_TRANSITION_TIME
    );
    window.addEventListener('resize', updateTargetRect);

    return () => {
      window.clearTimeout(transitionTimer);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [layoutKey, updateTargetRect]);

  const handleComplete = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);
    const completed = await onComplete();

    if (!completed) {
      setSubmitting(false);
    }
  }, [onComplete, submitting]);

  if (step === 0) return null;

  const frame1Title = t('s.assistenteteo.apresentacao.tituloframe1');
  const teoNamePosition = frame1Title.indexOf('Teo');
  const frame1TitlePrefix = frame1Title.substring(0, teoNamePosition);
  const frame1TitleSuffix = frame1Title.substring(teoNamePosition + 3);

  const bubblePosition =
    step === 2 && targetRect
      ? {
          top: Math.max(targetRect.bottom + 24, 88),
          right: Math.max(window.innerWidth - targetRect.right, 16),
        }
      : undefined;

  return (
    <S.Overlay
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-label="Apresentação do Teo"
    >
      <S.Character
        key={step}
        src={step === 1 ? TeoAcenando : TeoApontando}
        alt="Teo, assistente de atendimento da Abastek"
      />

      <S.SpeechBubble pointing={step === 2} style={bubblePosition}>
        <S.CloseButton
          size="small"
          aria-label="Fechar apresentação do Teo"
          onClick={handleComplete}
          disabled={submitting}
        >
          <Close />
        </S.CloseButton>

        {step === 1 ? (
          <>
            <S.Title>
              {frame1TitlePrefix}
              <strong>Teo</strong>
              {frame1TitleSuffix}
            </S.Title>
            <S.Message>
              {t('s.assistenteteo.apresentacao.mensagemframe1')}
            </S.Message>
          </>
        ) : (
          <>
            <S.Title>{t('s.assistenteteo.apresentacao.tituloframe2')}</S.Title>
            <S.Message>
              {t('s.assistenteteo.apresentacao.mensagemframe2')}
            </S.Message>
          </>
        )}

        <S.Actions>
          {step === 1 && (
            <Button
              type="button"
              onClick={handleComplete}
              disabled={submitting}
            >
              {t('s.assistenteteo.apresentacao.pular')}
            </Button>
          )}
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={step === 1 ? () => setStep(2) : handleComplete}
            disabled={submitting}
          >
            {step === 1
              ? t('s.assistenteteo.apresentacao.proximo')
              : t('s.assistenteteo.apresentacao.entendi')}
          </Button>
        </S.Actions>
      </S.SpeechBubble>

      {step === 2 && targetRect && (
        <S.TargetHighlight
          aria-hidden="true"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}
    </S.Overlay>
  );
}

TeoOnboarding.propTypes = {
  targetRef: PropTypes.shape({ current: PropTypes.instanceOf(HTMLElement) })
    .isRequired,
  layoutKey: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]),
  onComplete: PropTypes.func.isRequired,
  onStepChange: PropTypes.func.isRequired,
};

TeoOnboarding.defaultProps = {
  layoutKey: null,
};
